/* eslint-disable @typescript-eslint/no-shadow  */
import { toE164 } from '@/lib/dialer/phone';

// The API takes the key (value); reps see the label (text).
export const OUTCOMES = [
  {
    text: 'Connected',
    value: 'connected',
  },
  {
    text: 'Left voicemail',
    value: 'voicemail',
  },
  {
    text: 'No answer',
    value: 'no-answer',
  },
  {
    text: 'Wrong number',
    value: 'wrong-number',
  },
  {
    text: 'Call back later',
    value: 'call-back',
  },
];

export const DIAL_ERROR =
  "This number can't be dialed. Use a 10-digit US number or a full +country number.";

export const START_ERROR = "The call didn't start. Try again.";

// Click-to-call: Honey asks the API to start the call, and Dialpad rings the
// rep's Dialpad app, which carries the audio. Honey never sees the live call,
// so the rep presses Wrap up when they hang up.
// activeCall.status moves: calling -> ringing -> wrapup.
// In wrap-up the rep picks an outcome, adds notes and presses Done, which
// logs the call on the lead and clears it.
export const state = () => ({
  open: false,
  number: '',
  target: null,
  activeCall: null,
  error: '',
});

export const mutations = {
  setOpen(state, open) {
    state.open = open;
  },

  setNumber(state, number) {
    state.number = number;
  },

  setTarget(state, target) {
    state.target = target;
  },

  setError(state, error) {
    state.error = error;
  },

  callStarted(state, { phone, companyId, name }) {
    state.activeCall = {
      phone,
      companyId: companyId || null,
      name: name || null,
      status: 'calling',
      outcome: null,
      notes: '',
      logged: false,
    };
  },

  setStatus(state, status) {
    if (state.activeCall) {
      state.activeCall.status = status;
    }
  },

  setWrapUp(state, { outcome, notes }) {
    if (state.activeCall) {
      if (outcome !== undefined) {
        state.activeCall.outcome = outcome;
      }

      if (notes !== undefined) {
        state.activeCall.notes = notes;
      }
    }
  },

  callLogged(state) {
    if (state.activeCall) {
      state.activeCall.logged = true;
    }
  },

  clearCall(state) {
    state.activeCall = null;
    state.target = null;
  },
};

export const actions = {
  toggle({ state, commit }) {
    commit('setOpen', !state.open);
  },

  // Called from the leads table or the Call button. Resolves to an error
  // string when the call can't start; otherwise null. A call still waiting on
  // Done is logged as it stands first.
  async dial({ state, commit, dispatch }, { phone, companyId, name } = {}) {
    const e164 = toE164(phone);

    commit('setOpen', true);

    if (!e164) {
      return DIAL_ERROR;
    }

    if (state.activeCall && !state.activeCall.logged) {
      dispatch('logCall');
    }

    commit('setNumber', e164);
    commit(
      'setTarget',
      companyId
        ? {
            companyId,
            name,
          }
        : null
    );
    commit('callStarted', {
      phone: e164,
      companyId,
      name,
    });

    try {
      await this.$axios.post('/lead/dial', {
        phone: e164,
        ...(companyId ? { companyId } : {}),
      });
      commit('setStatus', 'ringing');

      return null;
    } catch (err) {
      commit('clearCall');

      return (
        (err.response && err.response.data && err.response.data.message) ||
        START_ERROR
      );
    }
  },

  // The keypad only types a number; the Dialpad app handles digits on a call.
  pressKey({ state, commit }, digit) {
    if (state.activeCall) {
      return;
    }

    commit('setNumber', `${state.number}${digit}`);
    commit('setTarget', null);
  },

  wrapUp({ commit }) {
    commit('setStatus', 'wrapup');
  },

  done({ commit, dispatch }) {
    const logged = dispatch('logCall');

    commit('clearCall');

    return logged;
  },

  // Logs the call and its wrap-up on the lead. Skipped when the lead isn't
  // known. Notes are the rep's free text: never log them to the console.
  logCall({ state, commit }) {
    const call = state.activeCall;

    if (!call || call.logged || !call.companyId) {
      return Promise.resolve();
    }

    commit('callLogged');

    return this.$axios
      .post(`/lead/${call.companyId}/call`, {
        direction: 'outbound',
        phone: call.phone,
        outcome: call.outcome,
        notes: call.notes,
      })
      .catch(() => {
        commit('setError', "The call couldn't be logged on the lead.");
      });
  },
};
